import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { 
  Table, 
  Body, 
  Cell, 
  Head, 
  HeaderCell, 
  HeaderRow, 
  Row 
} from "@zendeskgarden/react-tables";
import { Tag } from "@zendeskgarden/react-tags";
import { Dropdown, Select, Field, Label, Item, Menu } from "@zendeskgarden/react-dropdowns";
import { Spinner } from "@zendeskgarden/react-loaders";
import { Notification } from "@zendeskgarden/react-notifications";
import { Button } from "@zendeskgarden/react-buttons";
import { Input } from "@zendeskgarden/react-forms";
import { adminApi } from "../../api";
import { User } from "../../utils/auth";

const roleColors: Record<string, string> = {
  admin: "red",
  physician: "blue", 
  specialist: "green",
  "super-admin": "purple",
};

// Status tag component matching cases styling
const StatusTag = styled.div<{ status: 'completed' | 'pending' | 'reviewing' }>`
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#d4edda';
      case 'pending': return '#fff3cd';
      case 'reviewing': return '#cce5ff';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#155724';
      case 'pending': return '#856404';
      case 'reviewing': return '#0c5aa6';
      default: return '#495057';
    }
  }};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 590;
  display: inline-block;
  
  @media (max-width: 480px) {
    padding: 3px 6px;
    font-size: 11px;
  }
`;

const RoleTag = styled.div<{ role: 'admin' | 'physician' | 'specialist' | string }>`
  background: ${props => {
    switch (props.role.toLowerCase()) {
      case 'admin': return '#ffe6e6';
      case 'physician': return '#e6f3ff';
      case 'specialist': return '#f0e6ff';
      case 'super-admin': return '#ffe6f3';
      default: return '#f0f0f0';
    }
  }};
  color: ${props => {
    switch (props.role.toLowerCase()) {
      case 'admin': return '#cc0000';
      case 'physician': return '#0066cc';
      case 'specialist': return '#7a00cc';
      case 'super-admin': return '#cc007a';
      default: return '#666666';
    }
  }};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 590;
  display: inline-block;
  
  @media (max-width: 480px) {
    padding: 3px 6px;
    font-size: 11px;
  }
`;

// Styled components for consistent button design
const StyledButton = styled(Button)<{ variant?: 'primary' | 'secondary' }>`
  background-color: ${props => props.variant === 'primary' ? '#8D4F1B' : '#f8f9fa'} !important;
  border-color: ${props => props.variant === 'primary' ? '#8D4F1B' : '#d8dcde'} !important;
  color: ${props => props.variant === 'primary' ? 'white' : '#68737d'} !important;
  
  &:hover {
    background-color: ${props => props.variant === 'primary' ? '#703815' : '#e9ecef'} !important;
    border-color: ${props => props.variant === 'primary' ? '#703815' : '#ced4da'} !important;
  }
  
  &:active {
    background-color: ${props => props.variant === 'primary' ? '#5a2c10' : '#dee2e6'} !important;
    border-color: ${props => props.variant === 'primary' ? '#5a2c10' : '#ced4da'} !important;
  }
`;

const ModalField = styled.div`
  margin-bottom: 16px;
`;

const ModalLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: #2f3941;
  font-size: 14px;
`;

const ModalInput = styled(Input)`
  width: 100%;
  
  &:focus {
    border-color: #8D4F1B !important;
    box-shadow: 0 0 0 2px rgba(141, 79, 27, 0.2) !important;
  }
`;

// Modal overlay to ensure proper positioning
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'admin' | 'specialist' | 'physician'>('all');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [changingPage, setChangingPage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'admin' | 'specialist' | 'physician'>('admin');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    specialty: '',
    institution: '',
    professionalRegistration: '',
    yearsOfExperience: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers(1); // initial load
  }, []);

  // Refetch when tab changes (reset to first page)
  useEffect(() => {
    if (pagination.limit > 0) { // Only fetch if pagination is initialized
      fetchUsers(1, { reset: true });
    }
  }, [activeTab, pagination.limit]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Add keyboard accessibility for modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showModal]);

  const fetchUsers = async (page: number = pagination.currentPage, options: { reset?: boolean } = {}) => {
    if (options.reset) {
      setPagination(p => ({ ...p, currentPage: 1 }));
    }
    // If first load use loading spinner, else show subtle page change state
    const isInitial = users.length === 0 && page === 1;
    if (isInitial) setLoading(true); else setChangingPage(true);
    try {
      const roleParam = activeTab === 'all' ? undefined : (activeTab === 'admin' ? 'admin' : activeTab);
      const res = await adminApi.getAllUsers({
        page,
        limit: pagination.limit,
        role: roleParam
      });
      console.log('User API Response:', res); // Debug log
      const payload: any = res.data || res; // depending on wrapper
      const apiUsers = payload.users || payload.data?.users || [];
      const pag = payload.pagination || payload.data?.pagination || {};
      console.log('Pagination data:', pag); // Debug log
      const transformedUsers = apiUsers.map((user: any) => ({
        ...user,
        id: user._id || user.id,
        name: user.name || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName || user.lastName || '')
      }));
      setUsers(transformedUsers);
      setPagination(prev => ({
        ...prev,
        currentPage: pag.currentPage || page,
        totalPages: pag.totalPages || 1,
        totalUsers: pag.totalUsers || transformedUsers.length,
        hasNextPage: pag.hasNextPage || false,
        hasPrevPage: pag.hasPrevPage || false,
        // keep limit
      }));
      console.log('Updated pagination state:', {
        currentPage: pag.currentPage || page,
        totalPages: pag.totalPages || 1,
        totalUsers: pag.totalUsers || transformedUsers.length,
        hasNextPage: pag.hasNextPage || false,
        hasPrevPage: pag.hasPrevPage || false,
      }); // Debug log
    } catch (e: unknown) {
      console.error('Failed to fetch users:', e); // Debug log
      setNotification({ type: 'error', message: 'Failed to fetch users' });
    }
    if (isInitial) setLoading(false); else setChangingPage(false);
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await adminApi.updateUser(userId, { role: newRole });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, role: newRole as User["role"] } : u
        )
      );
      setNotification({ type: 'success', message: 'Role updated successfully' });
    } catch (e: unknown) {
      setNotification({ type: 'error', message: 'Failed to update role' });
    }
  };

  const openModal = (type: 'admin' | 'specialist' | 'physician') => {
    console.log('Opening modal for type:', type); // Debug log
    setModalType(type);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      specialty: '',
      institution: '',
      professionalRegistration: '',
      yearsOfExperience: '',
      phone: ''
    });
    setShowModal(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    console.log('Submitting form for modalType:', modalType); // Debug log
    console.log('Form data:', formData); // Debug log

    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        ...(modalType !== 'admin' && {
          specialty: formData.specialty,
          institution: formData.institution,
          professionalRegistration: formData.professionalRegistration,
          yearsOfExperience: formData.yearsOfExperience ? Number(formData.yearsOfExperience) : undefined,
        })
      };

      console.log('Final userData to send:', userData); // Debug log

      if (modalType === 'admin') {
        console.log('Creating admin...'); // Debug log
        await adminApi.createAdmin(userData);
        setNotification({ type: 'success', message: 'Admin created successfully' });
      } else if (modalType === 'specialist') {
        console.log('Creating specialist...'); // Debug log
        await adminApi.createSpecialist(userData);
        setNotification({ type: 'success', message: 'Specialist created successfully' });
      } else if (modalType === 'physician') {
        console.log('Creating physician...'); // Debug log
        await adminApi.createPhysician(userData);
        setNotification({ type: 'success', message: 'Physician created successfully' });
      }
      
      setShowModal(false);
      fetchUsers(); // Refresh the user list
    } catch (e: unknown) {
      console.error('Error creating user:', e); // Debug log
      setNotification({ 
        type: 'error', 
        message: `Failed to create ${modalType}` 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Currently relying on server-side filtering except 'super-admin' which is included with admin updates
  const displayedUsers = users;

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages || newPage === pagination.currentPage) return;
    fetchUsers(newPage);
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value, 10) || 10;
    setPagination(p => ({ ...p, limit: newLimit, currentPage: 1 }));
    // Use setTimeout to ensure state is updated before fetching
    setTimeout(() => {
      fetchUsers(1, { reset: true });
    }, 0);
  };

  const startIndex = (pagination.currentPage - 1) * pagination.limit + 1;
  const endIndex = startIndex + displayedUsers.length - 1;

  return (
    <div style={{ padding: 24 }}>
      <h2>User Management</h2>
      
      {notification && (
        <div style={{ marginBottom: 16 }}>
          <Notification type={notification.type}>
            {notification.message}
          </Notification>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ marginBottom: 24, display: 'flex', gap: 16, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <StyledButton 
            size="small" 
            variant={activeTab === 'all' ? 'primary' : 'secondary'}
            onClick={() => setActiveTab('all')}
          >
            All Users
          </StyledButton>
          <StyledButton 
            size="small" 
            variant={activeTab === 'admin' ? 'primary' : 'secondary'}
            onClick={() => setActiveTab('admin')}
          >
            Administrators
          </StyledButton>
          <StyledButton 
            size="small" 
            variant={activeTab === 'specialist' ? 'primary' : 'secondary'}
            onClick={() => setActiveTab('specialist')}
          >
            Specialists
          </StyledButton>
          <StyledButton 
            size="small" 
            variant={activeTab === 'physician' ? 'primary' : 'secondary'}
            onClick={() => setActiveTab('physician')}
          >
            Physicians
          </StyledButton>
        </div>
        
        {/* Add New Buttons */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <StyledButton 
            size="small" 
            variant="primary"
            onClick={() => openModal('admin')}
          >
            + Add Admin
          </StyledButton>
          <StyledButton 
            size="small" 
            variant="primary"
            onClick={() => openModal('specialist')}
          >
            + Add Specialist
          </StyledButton>
          <StyledButton 
            size="small" 
            variant="primary"
            onClick={() => openModal('physician')}
          >
            + Add Physician
          </StyledButton>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spinner />
        </div>
      ) : (
        <Table>
          <Head>
            <HeaderRow>
             
              <HeaderCell>Name</HeaderCell>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Role</HeaderCell>
              <HeaderCell>Role Tag</HeaderCell>
            </HeaderRow>
          </Head>
          <Body>
            {changingPage && (
              <Row>
                <Cell colSpan={5}>
                  <div style={{ display:'flex', justifyContent:'center', padding:16 }}>
                    <Spinner size="18" />
                  </div>
                </Cell>
              </Row>
            )}
            {!changingPage && displayedUsers.length === 0 && (
              <Row>
                <Cell colSpan={5}>
                  <div style={{ textAlign:'center', padding:24, color:'#68737d' }}>No users found.</div>
                </Cell>
              </Row>
            )}
            {!changingPage && displayedUsers.map((user) => (
              <Row key={user.id}>
             
                <Cell>
                  {user.name || 'N/A'}
                </Cell>
                <Cell>{user.email}</Cell>
                <Cell>
                  <Dropdown 
                  selectedItem={user.role}
                  onSelect={(selectedRole) => handleRoleChange(user.id, selectedRole)}
                  >
                  <Field>
                    <Select>{user.role}</Select>
                  </Field>
                   
                  </Dropdown>
                </Cell>
                <Cell>
                  <RoleTag role={user.role}>
                    {user.role.toUpperCase()}
                  </RoleTag>
                </Cell>
              </Row>
            ))}
          </Body>
        </Table>
      )}

      {/* Pagination Controls */}
      {!loading && users.length > 0 && (
        <div style={{ 
          display:'flex', 
          flexWrap:'wrap', 
          gap:16, 
          alignItems:'center', 
          justifyContent:'space-between', 
          marginTop:16, 
          borderTop:'1px solid #e9ebed', 
          paddingTop:16,
          borderRadius: '0 0 8px 8px',
          background: '#ffffff'
        }}>
          <div style={{ fontSize:14, color:'#68737d' }}>
            Showing {startIndex}-{endIndex} of {pagination.totalUsers} users
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <label style={{ fontSize:12, color:'#68737d', fontWeight: 600 }}>Rows per page:</label>
            <select 
              value={pagination.limit} 
              onChange={handleLimitChange} 
              style={{ 
                padding:'4px 8px', 
                fontSize:14, 
                border:'1px solid #d8dcde', 
                borderRadius:4,
                background: '#ffffff',
                color: '#2f3941'
              }}
            >
              {[10,25,50].map(sz => <option key={sz} value={sz}>{sz}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button 
              onClick={() => handlePageChange(pagination.currentPage - 1)} 
              disabled={!pagination.hasPrevPage} 
              style={pageButtonStyle(false, !pagination.hasPrevPage)}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
              const startPage = Math.max(1, pagination.currentPage - 2);
              const pageNumber = startPage + i;
              if (pageNumber <= pagination.totalPages) {
                return (
                  <button 
                    key={pageNumber} 
                    onClick={() => handlePageChange(pageNumber)} 
                    style={pageButtonStyle(pageNumber === pagination.currentPage, false)}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}
            <button 
              onClick={() => handlePageChange(pagination.currentPage + 1)} 
              disabled={!pagination.hasNextPage} 
              style={pageButtonStyle(false, !pagination.hasNextPage)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal for Adding New Users */}
      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, color: '#2f3941', fontSize: '18px', fontWeight: 600 }}>
                  Add New {modalType === 'admin' ? 'Administrator' : modalType === 'specialist' ? 'Specialist' : 'Physician'}
                </h3>
                <button 
                  onClick={() => setShowModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#68737d',
                    padding: '4px 8px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <form onSubmit={handleFormSubmit}>
                <ModalField>
                  <ModalLabel>First Name</ModalLabel>
                  <ModalInput
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </ModalField>
                
                <ModalField>
                  <ModalLabel>Last Name</ModalLabel>
                  <ModalInput
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </ModalField>
                
                <ModalField>
                  <ModalLabel>Email</ModalLabel>
                  <ModalInput
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </ModalField>
                
                <ModalField>
                  <ModalLabel>Password</ModalLabel>
                  <ModalInput
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </ModalField>

                <ModalField>
                  <ModalLabel>Phone</ModalLabel>
                  <ModalInput
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </ModalField>

                {(modalType === 'specialist' || modalType === 'physician') && (
                  <>
                    <ModalField>
                      <ModalLabel>Specialty</ModalLabel>
                      <ModalInput
                        value={formData.specialty}
                        onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                        required
                      />
                    </ModalField>
                    
                    <ModalField>
                      <ModalLabel>Institution</ModalLabel>
                      <ModalInput
                        value={formData.institution}
                        onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                      />
                    </ModalField>
                    
                    <ModalField>
                      <ModalLabel>Professional Registration</ModalLabel>
                      <ModalInput
                        value={formData.professionalRegistration}
                        onChange={(e) => setFormData({ ...formData, professionalRegistration: e.target.value })}
                      />
                    </ModalField>
                    
                    <ModalField>
                      <ModalLabel>Years of Experience</ModalLabel>
                      <ModalInput
                        type="number"
                        value={formData.yearsOfExperience}
                        onChange={(e) => setFormData({ ...formData, yearsOfExperience: e.target.value })}
                      />
                    </ModalField>
                  </>
                )}
              </form>
            </div>
            <div style={{ padding: '20px', borderTop: '1px solid #e9ecef', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <StyledButton variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </StyledButton>
              <StyledButton 
                variant="primary"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : `Create ${modalType === 'admin' ? 'Admin' : modalType === 'specialist' ? 'Specialist' : 'Physician'}`}
              </StyledButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  );
};

export default UserManagement;

// Simple page button styling function (avoids styled-components overhead for small control)
function pageButtonStyle(active: boolean, disabled: boolean): React.CSSProperties {
  return {
    padding: '6px 10px',
    border: '1px solid ' + (active ? '#8D4F1B' : '#d8dcde'),
    background: active ? '#8D4F1B' : '#fff',
    color: active ? '#fff' : disabled ? '#d8dcde' : '#2f3941',
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    minWidth: 40,
    transition: 'all 0.2s ease'
  };
}